import React, { useState } from 'react';
import Icons from './Icons';

// Pickup locations with charges
const pickupLocations = [
  { name: 'Airport', charge: 1100 },
  { name: 'Railway Station', charge: 600 },
  { name: 'City Bus Stand', charge: 400 },
  { name: 'Office Pickup', charge: 0 }
];

// Late fee policy constant - 10% of daily price per hour
const LATE_FEE_PERCENTAGE = 0.10; // 10% of daily price per hour
const LATE_FEE_POLICY_TEXT = "Late Return Policy – If the vehicle is returned after the selected return time, a late fee of 10% of the daily rental price will be charged per hour.";

const BookingModal = ({ isOpen, onClose, car, isDark }) => {
  // State variables as per requirements
  const [pickupLocation, setPickupLocation] = useState('');
  const [pickupLocationCharge, setPickupLocationCharge] = useState(0);
  const [pickupDate, setPickupDate] = useState(new Date().toISOString().split('T')[0]);
  const [pickupTime, setPickupTime] = useState('10:00'); // Default 10:00 AM in 24h format
  const [returnDate, setReturnDate] = useState('');
  const [returnTime, setReturnTime] = useState('10:00'); // Default 10:00 AM in 24h format
  const [validationError, setValidationError] = useState('');

  if (!isOpen || !car) return null;

  const pricePerDay = car.price_per_day || 0;
  const today = new Date().toISOString().split('T')[0];

  // Handle location selection
  const handleLocationChange = (e) => {
    const selectedLocation = e.target.value;
    setPickupLocation(selectedLocation);
    
    // Find the charge for the selected location
    const location = pickupLocations.find(loc => loc.name === selectedLocation);
    setPickupLocationCharge(location ? location.charge : 0);
  };

  // Convert 24h time to 12h format with AM/PM
  const formatTimeToAMPM = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Calculate duration in hours
  const calculateDuration = () => {
    if (!pickupDate || !returnDate || !pickupTime || !returnTime) return 0;

    const pickupDateTime = new Date(`${pickupDate}T${pickupTime}`);
    const returnDateTime = new Date(`${returnDate}T${returnTime}`);

    const diffInMs = returnDateTime - pickupDateTime;
    const diffInHours = diffInMs / (1000 * 60 * 60);

    return Math.max(0, diffInHours); // Ensure non-negative
  };

  // Calculate price based on hourly tiers
  // - 0-6 hours: 50% of daily price
  // - 6-12 hours: 65% of daily price  
  // - 12-24 hours: 100% (full daily price)
  // - 24-36 hours: 200% (double)
  // - 36-48 hours: 300% (triple), etc.
  const calculatePrice = () => {
    if (!returnDate || totalHours <= 0) return 0;
    
    // Get the daily rate
    const dailyRate = pricePerDay;
    
    // Calculate hours used
    const hours = totalHours;
    
    // Apply tiered pricing
    let price;
    if (hours <= 6) {
      // 0-6 hours: 50% of daily price
      price = dailyRate * 0.5;
    } else if (hours <= 12) {
      // 6-12 hours: 65% of daily price
      price = dailyRate * 0.65;
    } else if (hours <= 24) {
      // 12-24 hours: full daily price
      price = dailyRate;
    } else {
      // More than 24 hours: full day + percentage for extra hours
      const fullDays = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      
      // Calculate extra charge based on remaining hours
      let extraCharge = 0;
      if (remainingHours > 0 && remainingHours <= 6) {
        extraCharge = dailyRate * 0.5; // 50% for 1-6 extra hours
      } else if (remainingHours > 6 && remainingHours <= 12) {
        extraCharge = dailyRate * 0.65; // 65% for 7-12 extra hours
      } else if (remainingHours > 12) {
        extraCharge = dailyRate; // Full day for 13-24 extra hours
      }
      
      price = (dailyRate * fullDays) + extraCharge;
    }
    
    return Math.round(price * 100) / 100; // Round to 2 decimal places
  };

  // Price calculation including pickup charge
  const totalHours = calculateDuration();
  const totalDays = Math.ceil(totalHours / 24);
  const basePrice = Number(calculatePrice());
  const totalPrice = Number(basePrice) + Number(pickupLocationCharge);

  // Late fee calculation - 10% of daily price per hour
  const lateFeePerHour = pricePerDay * LATE_FEE_PERCENTAGE;

  // Validation function
  const validateForm = () => {
    // Reset error
    setValidationError('');

    // Pick-up location must not be empty
    if (!pickupLocation.trim()) {
      setValidationError('Please select a pick-up location.');
      return false;
    }

    // Pick-up date must be today or future
    if (!pickupDate || pickupDate < today) {
      setValidationError('Pick-up date must be today or a future date.');
      return false;
    }

    // Return date must be provided and must be same as or after pick-up date
    if (!returnDate) {
      setValidationError('Please select a return date.');
      return false;
    }

    if (returnDate < pickupDate) {
      setValidationError('Return date must be same as or after pick-up date.');
      return false;
    }

    // If return date equals pick-up date, return time must be after pick-up time
    if (returnDate && returnDate === pickupDate && returnTime <= pickupTime) {
      setValidationError('Return time must be after pick-up time when dates are the same.');
      return false;
    }

    // If no return time provided
    if (!returnTime) {
      setValidationError('Please select a return time.');
      return false;
    }

    return true;
  };

  // WhatsApp message generation
  const handleWhatsAppShare = () => {
    if (!validateForm()) {
      return;
    }

    let message = `*Brothers Car Rental - Booking Request*\n\n` +
      `*Car:* ${car.name}\n` +
      `*Pick-up Location:* ${pickupLocation}\n` +
      `*Pick-up Date & Time:* ${pickupDate} at ${formatTimeToAMPM(pickupTime)}\n`;

    // Add return date/time (required now)
    message += `*Return Date & Time:* ${returnDate} at ${formatTimeToAMPM(returnTime)}
`;

    // Add duration and price
    message += `*Rental Duration:* ${totalHours.toFixed(1)} hours (${totalDays} day${totalDays > 1 ? 's' : ''})
` +
      `*Price per Day:* ₹${pricePerDay.toLocaleString()}
` +
      `*Pickup Charge:* ₹${pickupLocationCharge.toLocaleString()}
` +
      `*Total Amount:* ₹${totalPrice.toLocaleString()}

` +
      `*Note:*
${LATE_FEE_POLICY_TEXT}
`;

    message += `\nPlease confirm availability.`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/919602360369?text=${encodedMessage}`, '_blank');
    onClose();
  };

  // Calculate min return date (same as pickup date)
  const minReturnDate = pickupDate || today;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      <div
        className={`relative z-50 w-full max-w-md p-6 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#1a1a1a]' : 'bg-white'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-2 rounded-full ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          <Icons.X />
        </button>

        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Book <span className="text-[#c9a962]">{car.name}</span>
        </h3>

        {/* Pick-up Location */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Pick-up Location <span className="text-red-500">*</span>
          </label>
          <select
            value={pickupLocation}
            onChange={handleLocationChange}
            onClick={(e) => e.stopPropagation()}
            required
            className={`w-full px-4 py-3 rounded-lg cursor-pointer ${isDark ? 'bg-[#0d0d0d] text-white border border-gray-700 focus:border-[#c9a962]' : 'bg-white text-gray-900 border border-gray-300 focus:border-[#c9a962]'} focus:outline-none transition-colors appearance-none`}
          >
            <option value="" disabled>Select pick-up location</option>
            {pickupLocations.map((location) => (
              <option key={location.name} value={location.name}>
                {location.name} {location.charge > 0 ? `(₹${location.charge})` : '(Free)'}
              </option>
            ))}
          </select>
        </div>

        {/* Pick-up Date */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Pick-up Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            min={today}
            value={pickupDate}
            onChange={(e) => {
              setPickupDate(e.target.value);
              // Reset return date if it's before new pickup date
              if (returnDate && e.target.value > returnDate) {
                setReturnDate(e.target.value);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.showPicker()}
            required
            className={`w-full px-4 py-3 rounded-lg cursor-pointer ${isDark ? 'bg-[#0d0d0d] text-white border border-gray-700 focus:border-[#c9a962]' : 'bg-white text-gray-900 border border-gray-300 focus:border-[#c9a962]'} focus:outline-none transition-colors`}
            style={{ colorScheme: isDark ? 'dark' : 'light' }}
          />
        </div>

        {/* Pick-up Time */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Pick-up Time <span className="text-red-500">*</span>
          </label>
          <input
            type="time"
            step="1800"
            value={pickupTime}
            onChange={(e) => setPickupTime(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.showPicker && e.target.showPicker()}
            required
            className={`w-full px-4 py-3 rounded-lg cursor-pointer ${isDark ? 'bg-[#0d0d0d] text-white border border-gray-700 focus:border-[#c9a962]' : 'bg-white text-gray-900 border border-gray-300 focus:border-[#c9a962]'} focus:outline-none transition-colors`}
            style={{ colorScheme: isDark ? 'dark' : 'light' }}
          />
        </div>

        {/* Return Date */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Return Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            min={minReturnDate}
            value={returnDate}
            onChange={(e) => setReturnDate(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.showPicker()}
            required
            className={`w-full px-4 py-3 rounded-lg cursor-pointer ${isDark ? 'bg-[#0d0d0d] text-white border border-gray-700 focus:border-[#c9a962]' : 'bg-white text-gray-900 border border-gray-300 focus:border-[#c9a962]'} focus:outline-none transition-colors`}
            style={{ colorScheme: isDark ? 'dark' : 'light' }}
          />
        </div>

        {/* Return Time */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Return Time <span className="text-red-500">*</span>
          </label>
          <input
            type="time"
            step="1800"
            value={returnTime}
            onChange={(e) => setReturnTime(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.showPicker && e.target.showPicker()}
            required
            className={`w-full px-4 py-3 rounded-lg cursor-pointer ${isDark ? 'bg-[#0d0d0d] text-white border border-gray-700 focus:border-[#c9a962]' : 'bg-white text-gray-900 border border-gray-300 focus:border-[#c9a962]'} focus:outline-none transition-colors`}
            style={{ colorScheme: isDark ? 'dark' : 'light' }}
          />
        </div>

        {/* Validation Error Message */}
        {validationError && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-red-500 text-sm">{validationError}</p>
          </div>
        )}

        {/* Duration and Price Display */}
        <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-[#0d0d0d] border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
          <div className="flex justify-between mb-2">
            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Price per day:</span>
            <span className="text-[#c9a962] font-semibold">₹{pricePerDay.toLocaleString('en-IN')}</span>
          </div>
          {returnDate && (
            <>
              <div className="flex justify-between mb-2">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Rental Duration:</span>
                <span className={isDark ? 'text-white' : 'text-gray-900'}>
                  {totalHours > 0 ? `${totalHours.toFixed(1)} hours (${totalDays} day${totalDays > 1 ? 's' : ''})` : 'Select dates & times'}
                </span>
              </div>
              <div className="flex justify-between mb-2">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Pickup Charge:</span>
                <span className={isDark ? 'text-white' : 'text-gray-900'}>
                  {pickupLocationCharge > 0 ? `₹${pickupLocationCharge.toLocaleString()}` : '₹0'}
                </span>
              </div>
              <div className={`flex justify-between pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total Price:</span>
                <span className="text-xl font-bold text-[#c9a962]">
                  {totalPrice > 0 ? `₹${(Math.round(totalPrice * 100) / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '₹0'}
                </span>
              </div>
            </>
          )}
          {!returnDate && (
            <p className={`text-sm mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Select return date to see total price
            </p>
          )}
        </div>

        {/* Late Fee Policy Display */}
        <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-[#1a1a1a] border border-yellow-600/50' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Icons.AlertCircle className="w-5 h-5 text-yellow-500" />
            <span className={`font-semibold ${isDark ? 'text-yellow-400' : 'text-yellow-800'}`}>Late Return Policy</span>
          </div>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-yellow-700'}`}>
            If the car is returned after the selected return time, a late fee of 10% of the daily rental price (₹{lateFeePerHour.toLocaleString()}) will be charged per hour.
          </p>
          {returnDate && totalPrice > 0 && (
            <div className={`mt-2 pt-2 border-t ${isDark ? 'border-gray-700' : 'border-yellow-200'}`}>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-yellow-700'}`}>
                Estimated Late Fee: <span className="font-semibold text-yellow-500">₹{lateFeePerHour.toLocaleString()}</span> per hour
              </span>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          onClick={handleWhatsAppShare}
          className="w-full py-3 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-semibold uppercase tracking-wider hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all duration-300 rounded-lg flex items-center justify-center gap-2"
        >
          <Icons.WhatsApp />
          Book via WhatsApp
        </button>
      </div>
    </div>
  );
};

export default BookingModal;
